import cn from 'classnames';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { type Control, Controller, type Validate, useFieldArray, useForm } from 'react-hook-form-latest';
import ReactSelect, { type SingleValue } from 'react-select';

import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import Button from '@/components/UI/Button';
import type {
  DataTableColumn,
  DataTableColumnApiId,
  DataTableWorkflow,
  DataTableWorkflowAction,
} from '@/components/UI/DataTable/DataTable.model';
import { ColumnName } from '@/components/UI/DataTableV2/headers/ColumnName/ColumnName';
import Radio from '@/components/UI/FormElements/Radio';
import Icon from '@/components/UI/Icon';
import { RouteService } from '@/support/RouteService';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import styles from './DataTableWorkflowConfigure.module.scss';

interface DataTableWorkflowConfigureProps {
  closeModal: () => void;
  columns: Array<DataTableColumn>;
  workflow: DataTableWorkflow;
  onWorkflowSave: (workflow: DataTableWorkflow) => void;
  onWorkflowClear: () => void;
}

interface ActionEntry {
  value: DataTableColumnApiId;
}

interface SelectColumnWorkflow {
  start: 'select_column';
  startColumn: DataTableColumnApiId;
}

interface ScanIdWorkflow {
  start: 'scan_id';
  firstAction: DataTableColumnApiId;
}

interface CommonWorkflow {
  actions: Array<ActionEntry>;
}

type WorkflowForm = (ScanIdWorkflow | SelectColumnWorkflow) & CommonWorkflow;

const dataTableColumnIdPattern = /^dtc_[a-zA-Z0-9]{10,22}$/;
const isDataTableColumnId: Validate<DataTableColumnApiId, WorkflowForm> = (value) =>
  dataTableColumnIdPattern.test(value ?? '') || 'Must be a valid column';

const fromWorkflow = (workflow: DataTableWorkflow): Partial<WorkflowForm> => {
  if (_isEmpty(workflow)) {
    return { start: 'scan_id' };
  }
  const [rootActions, ...otherActions] = workflow;
  switch (rootActions.type) {
    case 'OpenSearch': {
      const firstAction = rootActions.destination;
      const actions = otherActions.slice(1).reduce<Array<ActionEntry>>((acc, action) => {
        switch (action.type) {
          case 'SelectColumn':
          case 'End':
            acc.push({ value: action.source });
            break;
        }
        return acc;
      }, []);

      return { start: 'scan_id', firstAction, actions };
    }
    case 'NextAnimal': {
      const actions = otherActions.slice(1).reduce<Array<ActionEntry>>((acc, action) => {
        switch (action.type) {
          case 'SelectColumn':
          case 'End':
            acc.push({ value: action.source });
            break;
        }
        return acc;
      }, []);
      return { start: 'select_column', startColumn: rootActions.destination, actions };
    }
    default: {
      return { start: 'scan_id' };
    }
  }
};

export const DataTableWorkflowConfigure: FC<DataTableWorkflowConfigureProps> = ({
  columns,
  closeModal,
  workflow,
  onWorkflowSave,
  onWorkflowClear,
}) => {
  const { watch, formState, control, reset, handleSubmit } = useForm<WorkflowForm>({
    mode: 'onChange',
    defaultValues: fromWorkflow(workflow),
  });
  const {
    fields: actionFields,
    append: appendAction,
    remove: removeAction,
  } = useFieldArray<WorkflowForm, 'actions'>({ control, name: 'actions' });

  const formData = watch();

  // Exclude read-only and computed columns from workflow navigation (formulas, observations, calculated measurements)
  const selectableColumns = useMemo(
    () =>
      columns.filter(
        (column) =>
          !column.read_only &&
          !column.locked &&
          column.type !== 'observation' &&
          column.type !== 'formula' &&
          column.type !== 'sample' &&
          !(column.type === 'measurement' && column.measurement?.is_calculated)
      ),
    [columns]
  );

  const usedColumns = useMemo(() => {
    const result = new Set<DataTableColumnApiId>();
    if (formData.start === 'select_column' && _isNotEmpty(formData.startColumn)) {
      result.add(formData.startColumn);
    }
    if (formData.start === 'scan_id' && _isNotEmpty(formData.firstAction)) {
      result.add(formData.firstAction);
    }
    (formData.actions ?? []).reduce<Set<DataTableColumnApiId>>((acc, action) => {
      if (_notNil(action) && _isNotEmpty(action.value)) {
        acc.add(action.value);
      }
      return acc;
    }, result);

    return result;
  }, [formData]);

  const columnsById = useMemo(() => new Map(columns.map((col) => [col.id, col])), [columns]);

  const handleClear = () => {
    reset({ start: 'scan_id' });
    onWorkflowClear();
  };
  const onSubmit = (data: WorkflowForm) => {
    const rootAction: DataTableWorkflowAction =
      data.start === 'scan_id'
        ? {
            type: 'OpenSearch',
            source: 'search',
            destination: data.firstAction,
          }
        : {
            type: 'NextAnimal',
            source: 'next-animal',
            destination: data.startColumn,
          };
    const result = (data.actions ?? []).reduce<DataTableWorkflow>(
      (acc, entry) => {
        const source = acc?.[acc.length - 1]?.destination;
        if (_notNil(source) && source !== 'end') {
          acc.push({ type: 'SelectColumn', source, destination: entry.value });
        }
        return acc;
      },
      [rootAction]
    );
    const endSource = result?.[result.length - 1]?.destination;
    if (_notNil(endSource) && endSource !== 'end') {
      result.push({ type: 'End', source: endSource, destination: 'end' });
    }
    onWorkflowSave(result);
  };

  return (
    <ModalContainer size="narrow" data-test-component="DataTableWorkflowConfigure">
      <ModalHeader
        className="bb0 pa3"
        title="Navigation setup"
        subText="Define how to navigate through this DataTable."
        readMoreLink={RouteService.help('datatable_workflows')}
      />
      <form onSubmit={handleSubmit(onSubmit)} className="ma3">
        <div>
          <div className="f6 near-black">Start by:</div>
          <div className="mv3">
            <Controller
              name="start"
              defaultValue="scan_id"
              control={control}
              rules={{
                required: 'You must select an option to trigger start',
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  <Radio
                    id="scan_id"
                    name="start"
                    onChange={onChange}
                    value="scan_id"
                    checked={value === 'scan_id'}
                    key="scan_id"
                    label="Scanning an ID"
                    className="mb2"
                    index={0}
                    data-testid="datatable-workflow-configure__scan-id-radio"
                  />
                  <Radio
                    id="select_column"
                    name="start"
                    checked={value === 'select_column'}
                    onChange={onChange}
                    value="select_column"
                    key="select_column"
                    label="Choosing a specific column"
                    className="mb2"
                    index={1}
                    data-testid="datatable-workflow-configure__select-column-radio"
                  />
                </>
              )}
            />
            {formData.start === 'select_column' && (
              <div className="mh4">
                <ColumnSelect
                  control={control}
                  name="startColumn"
                  selectableColumns={selectableColumns}
                  usedColumns={usedColumns}
                  columnsById={columnsById}
                  defaultValue={formData.startColumn ?? ''}
                />
              </div>
            )}
          </div>
        </div>
        {(formData.start === 'scan_id' || (formData.start === 'select_column' && actionFields.length > 0)) && (
          <div>
            <div className="f6 near-black mb3">Pressing return will move to:</div>
            {formData.start === 'scan_id' && (
              <div className={cn('flex', styles.actionOptions)}>
                <ColumnSelect
                  control={control}
                  name="firstAction"
                  selectableColumns={selectableColumns}
                  usedColumns={usedColumns}
                  columnsById={columnsById}
                  defaultValue={formData.firstAction ?? ''}
                />
              </div>
            )}
            {actionFields.map((field, fieldIndex) => (
              <div key={field.id} className={cn('flex', styles.actionOptions)}>
                <ColumnSelect
                  control={control}
                  name={`actions.${fieldIndex}.value`}
                  selectableColumns={selectableColumns}
                  usedColumns={usedColumns}
                  columnsById={columnsById}
                  defaultValue={formData.actions?.[fieldIndex]?.value ?? ''}
                  removeAction={() => removeAction(fieldIndex)}
                />
              </div>
            ))}
          </div>
        )}
        <Button
          onClick={() => appendAction({ value: '' as DataTableColumnApiId })}
          className="br-pill"
          icon="add_new"
          paleBlue
          testId="datatable-workflow-configure__add-step"
        >
          <span>Add step</span>
        </Button>
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitBtnText="Save"
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: !formState.isValid || formState.isValidating,
          testId: 'datatable-workflow-configure__submit',
        }}
        cancelButtonProps={{
          testId: 'datatable-workflow-configure__cancel',
        }}
        asideComponent={
          <span style={{ margin: '0 auto', marginRight: 0 }}>
            <Button plain onClick={handleClear} testId="datatable-workflow-configure__clear">
              Clear setup
            </Button>
          </span>
        }
      />
    </ModalContainer>
  );
};

interface ColumnSelectProps {
  control: Control<WorkflowForm>;
  name: 'firstAction' | 'startColumn' | `actions.${number}.value`;
  selectableColumns: Array<DataTableColumn>;
  usedColumns: Set<DataTableColumnApiId>;
  columnsById: Map<DataTableColumnApiId, DataTableColumn>;
  defaultValue: DataTableColumnApiId;
  removeAction?: () => void;
}

const ColumnSelect: FC<ColumnSelectProps> = ({
  control,
  name,
  selectableColumns,
  usedColumns,
  columnsById,
  defaultValue,
  removeAction,
}) => {
  const formatOptionLabel = useCallback(
    (column: DataTableColumn): JSX.Element => <ColumnName column={column} columnsById={columnsById} />,
    [columnsById]
  );

  return (
    <>
      <div className={styles.nestedInput} />
      <Controller
        name={name}
        defaultValue={defaultValue}
        control={control}
        rules={{ validate: { isDataTableColumnId } }}
        render={({ field, fieldState }) => {
          const selectedColumn = selectableColumns.find((col) => col.id === field.value);
          const availableColumns = selectableColumns.filter(
            (col) => !usedColumns.has(col.id) || col.id === field.value
          );

          return (
            <ReactSelect<DataTableColumn>
              value={selectedColumn}
              options={availableColumns}
              isOptionSelected={(option) => option.id === field.value}
              onChange={(option: SingleValue<DataTableColumn>) => field.onChange(option?.id)}
              onBlur={field.onBlur}
              formatOptionLabel={formatOptionLabel}
              getOptionValue={(column: DataTableColumn) => column.id}
              getOptionLabel={(column: DataTableColumn) => column.name ?? column.id}
              placeholder="Select a column..."
              className={cn('w-100 mb3 flex-grow-1 ui__select f6', {
                mr4: _isNil(removeAction),
                input__error: fieldState.invalid,
              })}
              classNamePrefix="ui__select"
              styles={{
                control: (base) => ({
                  ...base,
                  height: 40,
                  minHeight: 40,
                }),
              }}
            />
          );
        }}
      />
      {_notNil(removeAction) && (
        <span
          className="ma0 pointer flex justify-center items-center"
          style={{ width: '40px', height: '40px' }}
          onClick={removeAction}
        >
          <Icon icon="close" width="12" height="12" className="mid-gray hover-dark-gray" />
        </span>
      )}
    </>
  );
};
